import type { LensTheme } from '@diligentcorp/atlas-theme-mui-lens';
import { Box, Button, TextField, Typography, useTheme } from '@mui/material';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from '@hello-pangea/dnd';
import React, { useState } from 'react';

const MAX_OPTION_LABEL_LENGTH = 140;

// New options staged in the UI have no server id yet; they carry a temp id
// (resolved to a real id when the parent flushes the add on Save).
export const TEMP_OPTION_ID_PREFIX = 'temp-';

export interface CustomFieldOption {
  id: string;
  customFieldId: string;
  label: string;
  displayOrder: number;
  isActive: boolean;
  createdAt: Date | string;
  updatedAt: Date | string;
}

interface CustomFieldOptionsEditorProps {
  customFieldId: string;
  options: CustomFieldOption[];
  onOptionsChange: (options: CustomFieldOption[]) => void;
}

const nowIso = () => new Date().toISOString();

export const CustomFieldOptionsEditor: React.FC<
  CustomFieldOptionsEditorProps
> = ({ customFieldId, options, onOptionsChange }) => {
  const { tokens } = useTheme<LensTheme>();
  const color = tokens.semantic.color;

  const [newOptionLabel, setNewOptionLabel] = useState('');
  const [addError, setAddError] = useState('');

  const commit = (next: CustomFieldOption[]) => {
    onOptionsChange(next);
  };

  const handleAddOption = () => {
    const trimmed = newOptionLabel.trim();
    if (!trimmed) {
      setAddError('Label cannot be empty');
      return;
    }

    if (trimmed.length > MAX_OPTION_LABEL_LENGTH) {
      setAddError(
        `Label must be ${MAX_OPTION_LABEL_LENGTH} characters or less`,
      );
      return;
    }

    const duplicate = options.some(
      (opt) =>
        opt.isActive && opt.label.toLowerCase() === trimmed.toLowerCase(),
    );
    if (duplicate) {
      setAddError('An option with this label already exists');
      return;
    }

    const nextDisplayOrder = options
      .filter((opt) => opt.isActive)
      .reduce((max, opt) => Math.max(max, opt.displayOrder + 1), 0);

    const newOption: CustomFieldOption = {
      id: `${TEMP_OPTION_ID_PREFIX}${nextDisplayOrder}-${trimmed}`,
      customFieldId,
      label: trimmed,
      displayOrder: nextDisplayOrder,
      isActive: true,
      createdAt: nowIso(),
      updatedAt: nowIso(),
    };

    commit([...options, newOption]);
    setNewOptionLabel('');
    setAddError('');
  };

  const handleRetireOption = (optionId: string) => {
    commit(
      options.map((opt) =>
        opt.id === optionId ? { ...opt, isActive: false } : opt,
      ),
    );
  };

  const handleRestoreOption = (optionId: string) => {
    // Restoring moves the option to the end of the active list so a stale
    // displayOrder can't make it reappear mid-list.
    const nextDisplayOrder = options
      .filter((opt) => opt.isActive)
      .reduce((max, opt) => Math.max(max, opt.displayOrder + 1), 0);

    commit(
      options.map((opt) =>
        opt.id === optionId
          ? { ...opt, isActive: true, displayOrder: nextDisplayOrder }
          : opt,
      ),
    );
  };

  const handleDragEnd = (result: DropResult) => {
    const { destination, source } = result;

    if (!destination) return;
    if (destination.index === source.index) return;

    const activeOptions = options.filter((opt) => opt.isActive);
    const reordered = Array.from(activeOptions);
    const [removed] = reordered.splice(source.index, 1);
    reordered.splice(destination.index, 0, removed);

    const renumbered = reordered.map((opt, index) => ({
      ...opt,
      displayOrder: index,
    }));

    const inactiveOptions = options.filter((opt) => !opt.isActive);
    commit([...renumbered, ...inactiveOptions]);
  };

  const activeOptions = options.filter((opt) => opt.isActive);
  const inactiveOptions = options.filter((opt) => !opt.isActive);

  return (
    <Box sx={{ mt: 2 }}>
      <Typography
        variant="subtitle2"
        sx={{ mb: 1, fontWeight: 600, color: color.type.default.value }}
      >
        Options
      </Typography>

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="options">
          {(provided) => (
            <Box
              {...provided.droppableProps}
              ref={provided.innerRef}
              sx={{ mb: 2 }}
            >
              {activeOptions.map((option, index) => (
                <Draggable
                  key={option.id}
                  draggableId={option.id}
                  index={index}
                >
                  {(provided, snapshot) => (
                    <Box
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 1.5,
                        mb: 1,
                        border: `1px solid ${color.ui.divider.default.value}`,
                        borderRadius: 1,
                        backgroundColor: snapshot.isDragging
                          ? color.surface.subtle.value
                          : color.surface.default.value,
                        '&:hover': {
                          backgroundColor: color.surface.subtle.value,
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          flex: 1,
                        }}
                      >
                        <span
                          className="uil uil-draggabledots"
                          style={{
                            color: color.type.muted.value,
                            fontSize: 18,
                          }}
                        />
                        <Typography variant="body2" sx={{ flex: 1 }}>
                          {option.label}
                        </Typography>
                      </Box>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleRetireOption(option.id)}
                        title="Retire option"
                      >
                        Retire
                      </Button>
                    </Box>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </Box>
          )}
        </Droppable>
      </DragDropContext>

      {inactiveOptions.length > 0 && (
        <Box sx={{ mb: 2 }}>
          {inactiveOptions.map((option) => (
            <Box
              key={option.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 1.5,
                mb: 1,
                border: `1px solid ${color.ui.divider.default.value}`,
                borderRadius: 1,
                backgroundColor: color.surface.subtle.value,
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography
                  variant="body2"
                  sx={{
                    textDecoration: 'line-through',
                    color: color.type.muted.value,
                  }}
                >
                  {option.label}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: color.type.muted.value }}
                >
                  Retired option
                </Typography>
              </Box>
              <Button
                size="small"
                variant="outlined"
                onClick={() => handleRestoreOption(option.id)}
                title="Restore option"
              >
                Restore
              </Button>
            </Box>
          ))}
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Add an option and press enter"
          value={newOptionLabel}
          onChange={(e) => {
            setNewOptionLabel(e.target.value);
            setAddError('');
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleAddOption();
            }
          }}
          error={Boolean(addError)}
          helperText={addError}
        />
        <Button
          variant="contained"
          onClick={handleAddOption}
          disabled={!newOptionLabel.trim()}
        >
          Add
        </Button>
      </Box>

      <Typography
        variant="caption"
        sx={{ mt: 1, display: 'block', color: color.type.muted.value }}
      >
        Retired options stay on cases that already use them and remain in
        exports but they cannot be selected on new entries. Changes are applied
        when you save.
      </Typography>
    </Box>
  );
};
